<!-- OpenAIParamers.vue -->
<template>
  <v-container fluid>
    <div v-if="selectedModelValue && selectedModelValue.includes('gpt-5')" class="text-center pa-4">
      <p class="text-h6">설정할 수 있는 파라미터가 없습니다.</p>
    </div>

    <template v-else>
      <v-text-field
        :model-value="currentValue.temperature"
        label="Temperature"
        type="number"
        :min="0"
        :max="2"
        step="0.1"
        variant="underlined"
        :rules="[
          (v) => (v >= 0 && v <= 2) || '0 이상 2 이하의 값을 입력하세요.',
        ]"
        hint="샘플링 온도로, 값이 높을수록 응답이 예측 불가능해지고 창의적인 결과가 나올 수 있습니다."
        persistent-hint
        required
        @update:modelValue="updateParameter('temperature', $event)"
      >
        <template #prepend-inner>
          <svg-icon class="text-field-icon" :path="icons.mdiThermometer" viewbox="0 0 24 24" />
        </template>
      </v-text-field>

      <v-text-field
        :model-value="currentValue.topP"
        label="Top P"
        type="number"
        :min="0"
        :max="1"
        step="0.01"
        variant="underlined"
        :rules="[
          (v) => (v >= 0 && v <= 1) || '0 이상 1 이하의 값을 입력하세요.',
        ]"
        hint="누적 확률 분포 기반의 샘플링을 제어합니다. 1에 가까울 수록 가능한 모든 단어를 고려하며, 낮을수록은 확률이 높은 단어를 사용합니다."
        persistent-hint
        required
        @update:modelValue="updateParameter('topP', $event)"
      >
        <template #prepend-inner>
          <svg-icon class="text-field-icon" :path="icons.mdiChartAreaspline" viewbox="0 0 24 24" />
        </template>
      </v-text-field>

      <v-text-field
        :model-value="currentValue.frequencyPenalty"
        label="Frequency Penalty"
        type="number"
        :min="0"
        :max="2"
        step="0.1"
        variant="underlined"
        :rules="[
          (v) => (v >= 0 && v <= 2) || '0 이상 2 이하의 값을 입력하세요.',
        ]"
        hint="단어의 반복을 제한합니다. 높을수록 동일한 단어가 반복될 가능성을 줄입니다."
        persistent-hint
        required
        @update:modelValue="updateParameter('frequencyPenalty', $event)"
      >
        <template #prepend-inner>
          <svg-icon class="text-field-icon" :path="icons.mdiRepeat" viewbox="0 0 24 24" />
        </template>
      </v-text-field>

      <v-text-field
        :model-value="currentValue.presencePenalty"
        label="Presence Penalty"
        type="number"
        :min="0"
        :max="2"
        step="0.1"
        variant="underlined"
        :rules="[
          (v) => (v >= 0 && v <= 2) || '0 이상 2 이하의 값을 입력하세요.',
        ]"
        hint="주제의 반복을 제한합니다. 높을수록 새로운 주제를 도입할 가능성을 높입니다."
        persistent-hint
        required
        @update:modelValue="updateParameter('presencePenalty', $event)"
      >
        <template #prepend-inner>
          <svg-icon class="text-field-icon" :path="icons.mdiHistory" viewbox="0 0 24 24" />
        </template>
      </v-text-field>

      <template v-if="showSeedControls">
        <v-row>
          <v-col cols="8">
            <v-text-field
              :model-value="currentValue.seed"
              label="Seed"
              type="number"
              :min="0"
              :max="4294967295"
              step="1"
              variant="underlined"
              hint="0을 제외한 시드 값을 입력하면 특기사항의 일관성을 확보할 수 있습니다."
              persistent-hint
              :disabled="currentValue.randomSeed"
              :rules="[
                (v) => (v >= 0 && v <= 4294967295) || '0 이상 4294967295 이하의 값을 입력하세요.',
                (v) => Number.isInteger(Number(v)) || '정수를 입력하세요.',
              ]"
              @update:modelValue="updateParameter('seed', $event)"
            >
              <template #prepend-inner>
                <svg-icon class="text-field-icon" :path="icons.mdiDiceMultiple" viewbox="0 0 24 24" />
              </template>
            </v-text-field>
          </v-col>
          <v-col cols="4" class="d-flex align-center random-seed-column">
            <div class="random-seed-option">
              <v-checkbox
                class="random-seed-checkbox"
                :model-value="currentValue.randomSeed"
                color="primary"
                label="Random Seed"
                hide-details
                @update:modelValue="updateParameter('randomSeed', $event)"
              />
              <div class="random-seed-hint">입력한 시드 값을 무시하고 랜덤 생성합니다.</div>
            </div>
          </v-col>
        </v-row>
      </template>
    </template>
  </v-container>
</template>

<script>
import icons from '@/icons';

export default {
  props: {
    modelValue: {
      type: Object,
      required: true,
    },
    selectedModelValue: {
      type: String,
      default: '',
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      icons,
    };
  },
  computed: {
    currentValue() {
      return this.modelValue || {};
    },
    showSeedControls() {
      return (
        !this.selectedModelValue ||
        (!this.selectedModelValue.includes('solar') && !this.selectedModelValue.includes('gpt-5'))
      );
    },
  },
  methods: {
    updateParameter(key, newValue) {
      const valueToUpdate = key === 'randomSeed' ? Boolean(newValue) : Number(newValue);
      this.$emit('update:modelValue', {
        ...this.currentValue,
        [key]: valueToUpdate,
      });
    },
  },
};
</script>

<style scoped>
.text-field-icon {
  opacity: 0.7;
}

.random-seed-column {
  width: 100%;
}

.random-seed-option {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
}

.random-seed-checkbox :deep(.v-input__control) {
  justify-content: flex-start;
}

.random-seed-checkbox :deep(.v-selection-control) {
  justify-content: flex-start;
  min-height: 32px;
}

.random-seed-checkbox :deep(.v-label) {
  line-height: 1.2;
}

.random-seed-hint {
  margin-top: -2px;
  margin-left: 0;
  font-size: 0.75rem;
  line-height: 1.2;
  color: rgba(0, 0, 0, 0.6);
}
</style>
